<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useI18n } from 'vue-i18n';
import CsatMetrics from '../CsatMetrics.vue';
import CsatTable from '../CsatTable.vue';
import CsatFilters from '../Csat/CsatFilters.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  since: { type: Date, required: true },
  until: { type: Date, required: true },
});

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isTeamsEnabled = computed(() =>
  isFeatureEnabledOnAccount.value(
    accountId.value,
    FEATURE_FLAGS.TEAM_MANAGEMENT
  )
);

const pageIndex = ref(0);
const userIds = ref([]);
const inbox = ref(null);
const team = ref(null);
const rating = ref(null);

const requestPayload = computed(() => ({
  from: Math.floor(props.since.getTime() / 1000),
  to: Math.floor(props.until.getTime() / 1000),
  user_ids: userIds.value,
  inbox_id: inbox.value,
  team_id: team.value,
  rating: rating.value,
}));

const getResponses = () => {
  store.dispatch('csat/get', {
    page: pageIndex.value + 1,
    ...requestPayload.value,
  });
};

const getAllData = () => {
  try {
    store.dispatch('csat/getMetrics', requestPayload.value);
    getResponses();
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  }
};

const onFilterChange = ({
  selectedAgents,
  selectedInbox,
  selectedTeam,
  selectedRating,
}) => {
  userIds.value = selectedAgents.map(el => el.id);
  inbox.value = selectedInbox?.id;
  team.value = selectedTeam?.id;
  rating.value = selectedRating?.value;
  getAllData();
};

const onPageNumberChange = newPageIndex => {
  pageIndex.value = newPageIndex;
  getResponses();
};

const downloadReports = () => {
  try {
    store.dispatch('csat/downloadCSATReports', {
      fileName: generateFileName({ type: 'csat', to: requestPayload.value.to }),
      ...requestPayload.value,
    });
  } catch {
    useAlert(t('REPORT.CSAT_REPORTS.DOWNLOAD_FAILED'));
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  getAllData();
});

watch(() => [props.since, props.until], getAllData);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <Button
        :label="t('CSAT_REPORTS.DOWNLOAD')"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadReports"
      />
    </div>
    <CsatFilters
      :since="since"
      :until="until"
      :show-team-filter="isTeamsEnabled"
      @filter-change="onFilterChange"
    />
    <CsatMetrics :filters="requestPayload" />
    <CsatTable :page-index="pageIndex" @page-change="onPageNumberChange" />
  </div>
</template>
